import type { ConsentsView } from "@tutor/protocol";
import { Icon } from "@tutor/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocale } from "../i18n";
import * as api from "./api";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// Consent history and re-consent to a new privacy notice. Withdrawal and export belong to
// workstream 8; until they ship the links say so.

const METHOD_KEYS: Readonly<Record<string, ParentStringKey>> = {
  verified_email_invited_alpha: "consent.method.verified_email_invited_alpha",
};

export function Consent(props: {
  profileId: string;
  refresh: number;
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { profileId, refresh, onAuthError } = props;
  const t = useParentT();
  const locale = useLocale();
  const [view, setView] = useState<ConsentsView | null>(null);
  const [message, setMessage] = useState<{ key: ParentStringKey; info: boolean }>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.getConsents(profileId);
    if (res.ok) setView(res.value);
    else if (!onAuthError(res.error)) setMessage({ key: errorKey(res.error), info: false });
  }, [profileId, onAuthError]);

  useEffect(() => {
    void load();
  }, [load, refresh]);

  const accept = async () => {
    if (view === null || busy) return;
    setBusy(true);
    const res = await api.acceptNotice(profileId, view.currentVersion);
    setBusy(false);
    if (!res.ok) {
      if (!onAuthError(res.error)) setMessage({ key: errorKey(res.error), info: false });
      return;
    }
    setMessage({ key: "consent.accepted", info: true });
    await load();
  };

  const date = new Intl.DateTimeFormat(locale, { dateStyle: "long" });

  const current = view?.consents.find((c) => c.current);
  const when = (iso: string) => {
    const at = Date.parse(iso);
    return Number.isFinite(at) ? date.format(at) : iso;
  };

  return (
    <div className="pm-consent">
      {message === undefined ? null : (
        <p
          className={message.info ? "parent-message parent-message-info" : "parent-message"}
          role={message.info ? "status" : "alert"}
        >
          {t(message.key)}
        </p>
      )}
      {view === null ? (
        message === undefined ? (
          <p className="parent-muted">{t("parent.loading")}</p>
        ) : null
      ) : (
        <>
          {view.upToDate ? (
            <div className="pm-status" data-tone="good">
              <span className="pm-status__icon">
                <Icon name="check" />
              </span>
              <div>
                <p className="pm-status__title">{t("consent.upToDate")}</p>
                {current === undefined ? null : (
                  <p className="parent-muted">
                    {t("consent.currentSince", {
                      version: current.noticeVersion,
                      date: when(current.grantedAt),
                    })}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="pm-status" data-tone="attention">
              <span className="pm-status__icon">
                <Icon name="info" />
              </span>
              <div className="pm-status__body">
                <p className="pm-status__title">
                  {t("consent.needed", { version: view.currentVersion })}
                </p>
                <button
                  type="button"
                  className="parent-button parent-button-primary"
                  disabled={busy}
                  onClick={() => void accept()}
                >
                  {t("consent.accept")}
                </button>
              </div>
            </div>
          )}
          <section className="pm-group" aria-labelledby="pm-consent-history">
            <h3 id="pm-consent-history" className="pm-group__title">
              {t("consent.title")}
            </h3>
            {view.consents.length === 0 ? (
              <p className="parent-muted">{t("consent.empty")}</p>
            ) : (
              <ul className="pm-history">
                {view.consents.map((c) => {
                  const methodKey = METHOD_KEYS[c.method];
                  return (
                    <li key={`${c.noticeVersion}-${c.grantedAt}`} className="pm-history__item">
                      <span className="pm-history__version">
                        {t("consent.version", { version: c.noticeVersion })}
                      </span>
                      <span className="parent-muted">
                        {when(c.grantedAt)}
                        {" · "}
                        {methodKey === undefined ? c.method : t(methodKey)}
                      </span>
                      {c.current ? <span className="pm-tag">{t("consent.current")}</span> : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section className="pm-group" aria-labelledby="pm-consent-rights">
            <h3 id="pm-consent-rights" className="pm-group__title">
              {t("consent.rights")}
            </h3>
            {(["consent.withdraw", "consent.export"] as const).map((key) => (
              <div key={key} className="pm-row">
                <div className="pm-row__line">
                  <span className="pm-row__label">{t(key)}</span>
                  <span className="pm-row__control">
                    <button type="button" className="pm-soon" disabled aria-disabled="true">
                      {t("consent.soon")}
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
